// #region Imports
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { isInExcludedLegalEntity } from "_shared/_views/tables/row-choice-excluded-legal-entity";
import EntityMember from "_shared/entity-members/entity-member";
import { FeeScheduleTemplateCategory } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import {
  FeeScheduleStaffingDecision,
  feeScheduleStaffingKey,
} from "_shared/fee-schedules/categories/fee-schedule-template-staffing";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo, useState } from "react";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";
// #endregion

// #region Styled Components
const Rows = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
  paddingTop: theme.spacing(1),
}));
const Row = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr",
  minWidth: 0,
  [theme.breakpoints.up("sm")]: {
    alignItems: "center",
    gridTemplateColumns: "1fr minmax(0, 18rem)",
  },
}));
const PersonName = styled(Typography)(() => ({
  fontWeight: 600,
  overflowWrap: "anywhere",
}));
// #endregion

// #region Types
export type ReconcileFeeScheduleStaffingDialogProps = {
  /** The people these rates name who are not on the proposal's team, each listed once. */
  strangers: readonly EntityMember[];
  /** The rates being applied, so each row can say which of them a person is named on. */
  categories: readonly FeeScheduleTemplateCategory[];
  /** Who the proposal can already bill, and therefore who a rate may be handed to instead. */
  teamMembers: readonly EntityMember[];
  /**
   * Whether this viewer may read the proposal's vendor team at all. A masked team cannot be added
   * to — `ProposalBuilder.setVendorTeam` and `withStaffingSeatedOnTeamRedline` both return their
   * input unchanged — and offers nobody to hand a rate to, so the dialog must not pretend either is
   * available.
   */
  teamIsMasked?: boolean;
  /**
   * The client's legal entity (#6091). Its people bill no rate of the proposal's, so a row for one
   * of them offers no "Add to the team", starts unassigned, and offers nobody from it instead —
   * neither from the team nor behind "Find someone else…".
   */
  excludedLegalEntityId?: Guid;
  /** Called once, with the decisions to apply, or with null when the user backed out. */
  onDecided: (decisions: ReadonlyMap<string, FeeScheduleStaffingDecision> | null) => void;
};

/** The value of one row's control. `browse` is an action rather than an answer; it never sticks. */
const BROWSE = "browse";
const ADD = "add";
const UNASSIGN = "unassign";
const SUBSTITUTE_PREFIX = "substitute:";
// #endregion

// #region Functions
/**
 * Everyone whose name the dialog has to show, at module scope because it depends on nothing in the
 * render: the people being asked about, the people a rate could be handed to, and any substitute
 * already chosen — a person picked out of the marketplace is on none of the other two lists.
 */
function userIdsToName(
  strangers: readonly EntityMember[],
  teamMembers: readonly EntityMember[],
  decisions: ReadonlyMap<string, FeeScheduleStaffingDecision>,
): Set<string> {
  const userIds = new Set<string>();
  for (const person of strangers) userIds.add(person.userId.value);
  for (const member of teamMembers) userIds.add(member.userId.value);
  for (const decision of decisions.values()) {
    if (decision.kind === "substitute") userIds.add(decision.substitute.userId.value);
  }
  return userIds;
}

/** The rates one person is named on, by name, for the sentence under their name. */
function ratesNaming(
  person: EntityMember,
  categories: readonly FeeScheduleTemplateCategory[],
): string[] {
  const names: string[] = [];
  for (const category of categories) {
    if (!category.teamMember?.isEqualTo(person)) continue;
    const name = category.name?.value;
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}
// #endregion

/**
 * Asks, one person at a time, what to do about the people a fee schedule template names whom this
 * proposal's vendor team does not have.
 *
 * The template belongs to a firm and may name anyone its author would put on a team; the proposal
 * may only bill somebody on its own vendor team, which `WorkAgreement.ValidateFeeSchedule` enforces
 * before a hire. #5237 requires the difference to be put to the user rather than resolved behind
 * their back — silently seating people on a negotiated team and silently discarding the staffing a
 * template exists to carry are both wrong — and each person gets their own answer, because a
 * template that names three people is rarely three of the same case.
 */
export default function ReconcileFeeScheduleStaffingDialog(
  props: Readonly<ReconcileFeeScheduleStaffingDialogProps>,
) {
  const { strangers, categories, onDecided, excludedLegalEntityId } = props;
  const teamIsMasked = props.teamIsMasked ?? false;
  const { openDialog, popDialog } = useDialog();

  function worksForTheClient(person: EntityMember): boolean {
    return isInExcludedLegalEntity(excludedLegalEntityId, person.legalEntityId);
  }

  // Nobody from the client's firm can be handed a rate, wherever they sit.
  const teamMembers = useMemo(
    () => props.teamMembers.filter((member) => !isInExcludedLegalEntity(excludedLegalEntityId, member.legalEntityId)),
    [props.teamMembers, excludedLegalEntityId],
  );

  // "Add them" is the default because it is the answer that keeps the rate meaning what the
  // template meant. Nothing is applied until Apply is pressed, so a default costs nothing. Where
  // adding them is not on offer, the rate starts unassigned.
  const [decisions, setDecisions] = useState<ReadonlyMap<string, FeeScheduleStaffingDecision>>(
    () =>
      new Map(
        strangers.map((person) => [
          feeScheduleStaffingKey(person),
          teamIsMasked || isInExcludedLegalEntity(excludedLegalEntityId, person.legalEntityId)
            ? ({ kind: "unassign" } as const)
            : ({ kind: "add" } as const),
        ]),
      ),
  );

  const userIds = useMemo(
    () => userIdsToName(strangers, teamMembers, decisions),
    [decisions, strangers, teamMembers],
  );
  const individualsQuery = useGetIndividualsByUserIdsQuery(userIds.size > 0 ? { userIds: [...userIds] } : skipToken);
  const namesByUserId = useMemo(
    () =>
      new Map(
        (Individual.fromJSONArray(individualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
          individual.userId.value,
          individual.fullName,
        ]),
      ),
    [individualsQuery.data],
  );

  // The same fallback the category editor uses for a person with no profile this reader may see.
  function nameOf(person: EntityMember): string {
    return namesByUserId.get(person.userId.value) ?? "Team member";
  }

  function decisionFor(person: EntityMember): FeeScheduleStaffingDecision {
    return decisions.get(feeScheduleStaffingKey(person)) ?? { kind: "unassign" };
  }

  function setDecision(person: EntityMember, decision: FeeScheduleStaffingDecision) {
    setDecisions((previous) => {
      const next = new Map(previous);
      next.set(feeScheduleStaffingKey(person), decision);
      return next;
    });
  }

  // A substitute picked out of the marketplace is on no list this dialog was handed, so its own
  // control has to keep offering them or the row would snap back to "Add to the team" on re-render.
  const substitutesChosen = useMemo(() => {
    const chosen: EntityMember[] = [];
    for (const decision of decisions.values()) {
      if (decision.kind !== "substitute") continue;
      if (teamMembers.some((member) => member.isEqualTo(decision.substitute))) continue;
      if (chosen.some((member) => member.isEqualTo(decision.substitute))) continue;
      chosen.push(decision.substitute);
    }
    return chosen;
  }, [decisions, teamMembers]);

  function valueFor(person: EntityMember): string {
    const decision = decisionFor(person);
    if (decision.kind === "add") return ADD;
    if (decision.kind === "unassign") return UNASSIGN;
    return `${SUBSTITUTE_PREFIX}${feeScheduleStaffingKey(decision.substitute)}`;
  }

  function browseForSubstitute(person: EntityMember) {
    openDialog({
      title: "Choose someone else",
      component: (
        <VendorTeamMemberSelection
          selectLeader={false}
          selectMember
          selectedTeamMemberUserIds={[]}
          excludedLegalEntityId={excludedLegalEntityId}
          onNewSelection={(individual) => setDecision(person, {
            kind: "substitute",
            substitute: individual.toEntityMember(),
          })}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: { display: "flex", minHeight: "60vh" },
      MuiProps: { fullWidth: true, maxWidth: "lg" },
    });
  }

  function handleRowChanged(person: EntityMember, value: string) {
    if (value === BROWSE) {
      browseForSubstitute(person);
      return;
    }
    if (value === ADD) {
      setDecision(person, { kind: "add" });
      return;
    }
    if (value === UNASSIGN) {
      setDecision(person, { kind: "unassign" });
      return;
    }
    const key = value.slice(SUBSTITUTE_PREFIX.length);
    const substitute =
      teamMembers.find((member) => feeScheduleStaffingKey(member) === key) ??
      substitutesChosen.find((member) => feeScheduleStaffingKey(member) === key);
    if (substitute) setDecision(person, { kind: "substitute", substitute });
  }

  /**
   * What one row may answer, built as a list rather than inline so a withheld answer is absent
   * rather than `false` — MUI reads a Select's children to render the closed field's label.
   */
  function answersFor(person: EntityMember): Array<{ value: string; label: string }> {
    const answers: Array<{ value: string; label: string }> = [];
    if (teamIsMasked) {
      // Neither adding nor substituting can be carried out against a team this viewer cannot
      // read, so neither is offered.
      answers.push({ value: UNASSIGN, label: "Leave the rate unassigned" });
      return answers;
    }
    if (!worksForTheClient(person)) answers.push({ value: ADD, label: "Add to the team" });
    for (const member of [...teamMembers, ...substitutesChosen]) {
      answers.push({
        value: `${SUBSTITUTE_PREFIX}${feeScheduleStaffingKey(member)}`,
        label: `${nameOf(member)} instead`,
      });
    }
    answers.push({ value: BROWSE, label: "Find someone else…" });
    answers.push({ value: UNASSIGN, label: "Leave the rate unassigned" });
    return answers;
  }

  const isOne = strangers.length === 1;

  // #region Render
  return (
    <div>
      <Typography variant="body2">
        {isOne
          ? "This fee schedule names somebody who is not on this proposal's vendor team. An hourly rate can only be billed by somebody on the team."
          : "This fee schedule names people who are not on this proposal's vendor team. An hourly rate can only be billed by somebody on the team."}
      </Typography>
      {teamIsMasked && (
        <Alert severity="info" sx={{ marginTop: 2 }}>
          You cannot read this proposal&apos;s vendor team, so nobody can be added to it or chosen
          instead. The rates can still be applied without naming anyone.
        </Alert>
      )}
      <Rows>
        {strangers.map((person, index) => {
          const rates = ratesNaming(person, categories);
          return (
            <div key={feeScheduleStaffingKey(person)}>
              {index > 0 && <Divider sx={{ marginBottom: 2 }} />}
              <Row>
                <div>
                  <PersonName variant="body1">{nameOf(person)}</PersonName>
                  {rates.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {rates.join(", ")}
                    </Typography>
                  )}
                  {worksForTheClient(person) && (
                    <Typography variant="body2" color="warning.dark">
                      Works for the client, so cannot bill a rate. Choose somebody else or leave it unassigned.
                    </Typography>
                  )}
                </div>
                <TextField
                  select
                  size="small"
                  label={`What to do about ${nameOf(person)}`}
                  // The label names the person (#6330).
                  slotProps={{ inputLabel: { className: SESSION_RECORDING_MASK_CLASS } }}
                  value={valueFor(person)}
                  onChange={(event) => handleRowChanged(person, event.target.value)}
                >
                  {answersFor(person).map((answer) => (
                    <MenuItem key={answer.value} value={answer.value}>
                      {answer.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Row>
            </div>
          );
        })}
      </Rows>
      <DialogActions sx={{ paddingX: 0 }}>
        <Button color="inherit" onClick={() => onDecided(null)}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => onDecided(decisions)}>
          Apply
        </Button>
      </DialogActions>
    </div>
  );
  // #endregion
}
